import type {
  JsonApiEmployeeIncluded,
  JsonApiServiceIncluded,
  JsonApiLocationIncluded,
} from '../types/appointments.ts';
import type { LocationNameMode } from '../types/settings.ts';
import type { FreshaLocation } from '../types/fresha.ts';
import { resolveLocationName } from './locationResolution.ts';

export interface ResolvedBookingEntities {
  staff?: string;
  service?: string;
  location?: string;
  locationId?: string;
  appointmentDate?: string;
}

/**
 * Extracts strongly-typed entities (employee, service, location, appointment date)
 * from JSON:API booking detail responses without using any regex or heuristics.
 */
export function extractRelationalBookingEntities(
  bookingRes: any,
  fallbackSaleItem?: any,
  locations: FreshaLocation[] = [],
  locationAliases: Record<string, string> = {},
  locationNameMode: LocationNameMode = 'alias'
): ResolvedBookingEntities {
  if (!bookingRes) {
    return {
      service: fallbackSaleItem?.attributes?.name,
    };
  }

  const included: any[] = Array.isArray(bookingRes?.data?.included)
    ? bookingRes.data.included
    : Array.isArray(bookingRes?.included)
    ? bookingRes.included
    : [];

  const emp = included.find(
    (x: any): x is JsonApiEmployeeIncluded => x?.type === 'employees'
  );
  const srv = included.find(
    (x: any): x is JsonApiServiceIncluded => x?.type === 'services'
  );
  const loc = included.find(
    (x: any): x is JsonApiLocationIncluded => x?.type === 'locations'
  );

  const staff = emp?.attributes?.name;
  const service =
    srv?.attributes?.name ||
    srv?.attributes?.['item-name'] ||
    srv?.attributes?.['service-name'] ||
    fallbackSaleItem?.attributes?.name ||
    fallbackSaleItem?.attributes?.['item-name'] ||
    fallbackSaleItem?.attributes?.['service-name'];
  const rawLocation = loc?.attributes?.['internal-name'] || loc?.attributes?.name;
  const locationId = loc?.id ? String(loc.id) : undefined;
  const location = resolveLocationName(
    locationId,
    rawLocation,
    locations,
    locationAliases,
    locationNameMode
  );

  const bDate =
    bookingRes?.data?.data?.attributes?.['start-wall']?.date ||
    bookingRes?.data?.attributes?.['start-wall']?.date;

  return {
    staff,
    service,
    location: location || rawLocation,
    locationId,
    appointmentDate: bDate,
  };
}

/**
 * Resolves the primary booking ID linked to a sale item via JSON:API relationships.
 */
export function extractBookingIdFromSale(salesResOrData: any): string | null {
  const salesIncluded =
    salesResOrData?.data?.included || salesResOrData?.included || [];
  const firstSaleItem = Array.isArray(salesIncluded)
    ? salesIncluded.find(
        (x: any) =>
          x?.type === 'sale-items' ||
          x?.type === 'services' ||
          x?.type === 'line-items'
      )
    : null;

  return firstSaleItem?.relationships?.booking?.data?.id || null;
}

/**
 * Resolves the service specialist, service name, and booking ID directly from
 * JSON:API sale and line item relationships without string heuristics.
 */
export function extractRelationalSaleItemEntities(
  sale: any,
  salesIncluded: any[] = []
): {
  bookingId?: string;
  staff?: string;
  service?: string;
  saleItem?: any;
} {
  if (!sale) return {};

  const saleItemRel =
    sale?.relationships?.['sale-items']?.data ||
    sale?.relationships?.['line-items']?.data ||
    sale?.relationships?.items?.data ||
    [];

  const saleItemIds = Array.isArray(saleItemRel)
    ? saleItemRel.map((r: any) => String(r.id))
    : saleItemRel?.id
    ? [String(saleItemRel.id)]
    : [];

  const matchedSaleItem = Array.isArray(salesIncluded)
    ? salesIncluded.find(
        (x: any) =>
          (saleItemIds.length > 0 && saleItemIds.includes(String(x.id))) ||
          x?.type === 'sale-items' ||
          x?.type === 'services' ||
          x?.type === 'line-items'
      )
    : null;

  const bookingId =
    matchedSaleItem?.relationships?.booking?.data?.id ||
    sale?.relationships?.booking?.data?.id ||
    sale?.attributes?.['booking-id'] ||
    sale?.attributes?.['appointment-id'];

  // Specialist from line item employee relationship
  const itemEmpId = matchedSaleItem?.relationships?.employee?.data?.id;
  const empEntity =
    itemEmpId && Array.isArray(salesIncluded)
      ? salesIncluded.find(
          (x: any) => x?.type === 'employees' && String(x.id) === String(itemEmpId)
        )
      : null;

  const staff =
    empEntity?.attributes?.name ||
    matchedSaleItem?.attributes?.['employee-name'] ||
    matchedSaleItem?.attributes?.['staff-name'] ||
    matchedSaleItem?.attributes?.employee?.name ||
    undefined;

  const service =
    matchedSaleItem?.attributes?.name ||
    matchedSaleItem?.attributes?.['item-name'] ||
    matchedSaleItem?.attributes?.['service-name'] ||
    sale?.attributes?.['service-name'] ||
    sale?.attributes?.['item-name'] ||
    undefined;

  return {
    bookingId: bookingId ? String(bookingId) : undefined,
    staff,
    service,
    saleItem: matchedSaleItem,
  };
}

